// apps/web/src/lib/survey-config.ts
//
// Single source of truth für alle Survey-Fragen.
// Änderungen hier wirken sich automatisch auf UI + Validierung aus.
// Die `id` jeder Frage ist die DB-Key — nach erstem Deploy NICHT mehr ändern.
// MultiSelect mit allowOther: wenn keine Option passt, kann der "Sonstiges"-Freitext
// den minSelections-Bedarf alleine erfüllen.

export type QuestionOption = {
  value: string;
  label: string;
};

export type Question =
  | {
      id: string;
      type: "shortText";
      title: string;
      subtitle?: string;
      placeholder?: string;
      required?: boolean;
      maxLength?: number;
    }
  | {
      id: string;
      type: "longText";
      title: string;
      subtitle?: string;
      placeholder?: string;
      required?: boolean;
      minLength?: number;
      maxLength?: number;
    }
  | {
      id: string;
      type: "singleSelect";
      title: string;
      subtitle?: string;
      options: QuestionOption[];
      required?: boolean;
    }
  | {
      id: string;
      type: "multiSelect";
      title: string;
      subtitle?: string;
      options: QuestionOption[];
      allowOther?: boolean;
      required?: boolean;
      minSelections?: number;
    }
  | {
      id: string;
      type: "slider";
      title: string;
      subtitle?: string;
      min: number;
      max: number;
      step?: number;
      defaultValue?: number;
      minLabel: string;
      maxLabel: string;
      required?: boolean;
    };

export const SURVEY_META = {
  title: "Bevor wir loslegen",
  intro:
    "Hi! Bevor wir mit dem ersten Workshop starten, ein paar Fragen, damit ich den Einstieg gut auf euch zuschneiden kann. Es gibt keine richtigen oder falschen Antworten — Mehrfachauswahl ist überall erlaubt, und falls nichts passt, gibt's ein Freitextfeld. Dauert ungefähr 3 Minuten.",
  outroTitle: "Danke!",
  outroBody:
    "Das war's schon. Ich melde mich mit dem Termin und ein paar Vorab-Infos. Bis bald ✌️",
} as const;

export const SURVEY_QUESTIONS: readonly Question[] = [
  {
    id: "motivation",
    type: "multiSelect",
    title: "Was hat dich dazu gebracht, dir das zuzutrauen?",
    subtitle: "Was zieht dich gerade ans Programmieren? Mehrfachauswahl, Sonstiges möglich.",
    options: [
      { value: "concrete_idea", label: "Ich habe eine konkrete Idee, die ich bauen will" },
      { value: "less_dependent", label: "Ich will weniger abhängig von Devs sein" },
      { value: "leverage_ai", label: "Ich nutze KI viel und will mehr aus ihr rausholen" },
      { value: "team_understanding", label: "Im Team beim Thema Code mitreden können" },
      { value: "career_shift", label: "Karriere-Wechsel oder neue berufliche Richtung" },
      { value: "curiosity", label: "Reine Neugier — fühlt sich gerade richtig an" },
      { value: "future_proof", label: "Ich merke, ohne wird's bald schwierig" },
    ],
    allowOther: true,
    required: true,
    minSelections: 1,
  },
  {
    id: "goal_in_six_months",
    type: "multiSelect",
    title: "Was würdest du in einem halben Jahr gerne können?",
    subtitle: "Mehrfachauswahl. Wähl alles aus, was dich anspricht.",
    options: [
      { value: "small_tools", label: "Kleine Tools für mich selbst bauen" },
      { value: "websites", label: "Webseiten oder Web-Apps bauen" },
      { value: "data_analysis", label: "Daten auswerten / Reports automatisieren" },
      { value: "ai_agents", label: "KI-Agenten oder Bots bauen" },
      { value: "read_code", label: "Code von Kolleg:innen verstehen können" },
      { value: "monetize", label: "Etwas Eigenes bauen, das ich verkaufen oder verbreiten kann" },
      { value: "automation", label: "Wiederkehrende Aufgaben automatisieren" },
    ],
    allowOther: true,
    required: true,
    minSelections: 1,
  },
  {
    id: "mental_model",
    type: "multiSelect",
    title: "Was glaubst du, was Programmieren eigentlich ist?",
    subtitle:
      "Welche Bilder kommen dir zuerst in den Kopf? Mehrfach erlaubt. Es gibt keine richtige Antwort — eher dein Bauchgefühl.",
    options: [
      { value: "precise_instructions", label: "Eine sehr genaue Anleitung an eine Maschine" },
      { value: "language_with_machine", label: "Eine Sprache, mit der ich Computern Sachen sage" },
      { value: "logic_puzzle", label: "Logik-Puzzles, die schrittweise gelöst werden" },
      { value: "lego_building", label: "Bausteine kombinieren, die andere schon gebaut haben" },
      { value: "recipe", label: "Wie ein Kochrezept, nur für den Computer" },
      { value: "math_in_action", label: "Mathematik in Aktion" },
      { value: "no_clear_picture", label: "Ehrlich? Eher ein Rätsel — ich habe kein klares Bild" },
    ],
    allowOther: true,
    required: true,
    minSelections: 1,
  },
  {
    id: "ai_experience",
    type: "singleSelect",
    title: "Hast du in den letzten 4 Wochen mit ChatGPT, Claude, Copilot o.ä. etwas bauen lassen?",
    options: [
      { value: "none", label: "Nichts in dieser Richtung" },
      { value: "text_only", label: "Nur Texte, E-Mails, Übersetzungen — keinen Code" },
      { value: "code_failed", label: "Code probiert — hat aber nicht funktioniert" },
      { value: "code_worked", label: "Code probiert — und es hat tatsächlich geklappt" },
      { value: "regularly", label: "Ich nutze das regelmäßig" },
    ],
    required: true,
  },
  {
    id: "ai_experience_story",
    type: "longText",
    title: "Falls du Code mit KI probiert hast: erzähl kurz davon.",
    subtitle: "Optional. Was wolltest du bauen, was ist passiert? Zwei, drei Sätze reichen.",
    placeholder: "Ich wollte … und dann …",
    required: false,
  },
  {
    id: "dream_project",
    type: "multiSelect",
    title: "Welche Art von Projekt würdest du in den nächsten 8 Wochen am liebsten bauen?",
    subtitle:
      "Klein und nützlich (Excel-Auswertung) ist genauso gut wie groß und wild (eigener Bot). Mehrfach möglich, Sonstiges für was Spezifisches.",
    options: [
      { value: "personal_tool", label: "Ein eigenes kleines Tool für meinen Alltag" },
      { value: "web_app", label: "Eine Web-App mit konkretem Zweck" },
      { value: "chat_bot", label: "Ein Bot (Discord, Telegram, Slack o.ä.)" },
      { value: "ai_app", label: "Eine KI-Anwendung mit Chat-Oberfläche" },
      { value: "data_dashboard", label: "Ein Daten-Dashboard / eine Auswertung" },
      { value: "browser_extension", label: "Eine Browser-Extension" },
      { value: "mobile_app", label: "Eine Mobile-App" },
      { value: "spreadsheet_automation", label: "Eine Excel-/Sheets-Automatisierung" },
      { value: "mcp_integration", label: "Eine Integration für meinen KI-Assistenten (z. B. MCP)" },
    ],
    allowOther: true,
    required: true,
    minSelections: 1,
  },
  {
    id: "fears",
    type: "multiSelect",
    title: "Wovor hast du am meisten Bammel?",
    subtitle: "Mehrfachauswahl möglich. Hilft mir, den Workshop entsprechend zu gestalten.",
    options: [
      { value: "math", label: "Dass ich Mathe brauche und das nicht kann" },
      { value: "english", label: "Englisch — viele Begriffe, viele Docs" },
      { value: "looking_dumb", label: "Vor anderen blöd dazustehen, wenn ich was nicht checke" },
      { value: "too_complex", label: "Dass es einfach zu kompliziert wird" },
      { value: "not_enough_time", label: "Dass ich neben dem Alltag nicht genug Zeit habe" },
      { value: "breaking_things", label: "Dass ich am Computer was kaputt mache" },
      { value: "nothing", label: "Eigentlich nichts, ich bin entspannt" },
    ],
    allowOther: true,
    required: true,
    minSelections: 1,
  },
  {
    id: "terminal_comfort",
    type: "slider",
    title: "Wie wohl fühlst du dich mit dem Terminal / der Kommandozeile?",
    subtitle:
      "Das schwarze Fenster mit dem blinkenden Cursor. 1 = noch nie geöffnet, 10 = mein zweites Zuhause.",
    min: 1,
    max: 10,
    step: 1,
    defaultValue: 3,
    minLabel: "Was ist das?",
    maxLabel: "Tägliches Brot",
    required: true,
  },
  {
    id: "operating_system",
    type: "singleSelect",
    title: "Welches Betriebssystem nutzt du?",
    subtitle: "Für die Setup-Anleitung beim ersten Workshop.",
    options: [
      { value: "macos", label: "macOS" },
      { value: "windows", label: "Windows" },
      { value: "linux", label: "Linux" },
      { value: "unsure", label: "Keine Ahnung ehrlich gesagt" },
    ],
    required: true,
  },
  {
    id: "anything_else",
    type: "longText",
    title: "Gibt es noch irgendwas, das ich wissen sollte, bevor wir loslegen?",
    subtitle:
      "Optional. Zeitliche Einschränkungen, frühere Erfahrungen, Lerngewohnheiten — alles, was nicht oben gepasst hat.",
    placeholder: "Eine Sache wäre noch …",
    required: false,
  },
] as const;
